import * as Sentry from '@sentry/react';

// Ambientes que reportam ao Sentry. Fora deles (local, test, preview), o SDK fica inerte.
const REPORTING_STAGES = ['production', 'homolog'];

const TRACES_SAMPLE_RATE = {
  production: 0.2,
  homolog: 1.0,
};

// Hostnames/IPs de dev local — nome, loopback ou rede privada (acessar o `yarn start`
// pelo celular via 192.168.x cai aqui). NODE_ENV não serve de guard alternativo: o
// build de HOMOLOG roda com --mode development e seria desligado junto.
const LOCAL_HOST_PATTERNS = [
  /^localhost$/i,
  /^127\./,
  /^\[::1\]$/,
  /^0\.0\.0\.0$/,
  /^10\./,
  /^192\.168\./,
  /^172\.(1[6-9]|2\d|3[01])\./,
];

const isHostLocal = (hostname) => LOCAL_HOST_PATTERNS.some((padrao) => padrao.test(hostname || ''));

// Redige documentos (CPF/CNPJ/cartão/telefone: qualquer sequência de 11+ dígitos) em
// textos que saem para o Sentry — URLs de request carregam documento no path
// (ex.: /contracts/document/<cpf>). Ids internos (<= 10 dígitos) passam intactos.
// Guardrail do repo: não enviar CPF/telefone/token para log, Sentry ou Amplitude.
const DOCUMENTO_REGEX = /\d{11,}/g;

const redigirDocumentos = (texto) => (typeof texto === 'string' ? texto.replace(DOCUMENTO_REGEX, '[doc]') : texto);

// Aplica a redação nos campos de evento que carregam URL/nome de transação.
const redigirEvento = (event) => {
  if (event.transaction) {
    event.transaction = redigirDocumentos(event.transaction);
  }
  if (event.message) {
    event.message = redigirDocumentos(event.message);
  }
  if (event.exception && Array.isArray(event.exception.values)) {
    event.exception.values.forEach((excecao) => {
      if (excecao.value) {
        // eslint-disable-next-line no-param-reassign
        excecao.value = redigirDocumentos(excecao.value);
      }
    });
  }
  if (event.request && event.request.url) {
    event.request.url = redigirDocumentos(event.request.url);
  }
  if (Array.isArray(event.spans)) {
    event.spans.forEach((span) => {
      if (span.description) {
        // eslint-disable-next-line no-param-reassign
        span.description = redigirDocumentos(span.description);
      }
    });
  }
  return event;
};

const redigirBreadcrumb = (breadcrumb) => {
  const limpo = { ...breadcrumb };
  if (limpo.message) {
    limpo.message = redigirDocumentos(limpo.message);
  }
  if (limpo.data && typeof limpo.data.url === 'string') {
    limpo.data = { ...limpo.data, url: redigirDocumentos(limpo.data.url) };
  }
  return limpo;
};

// Parâmetros injetáveis para teste; em runtime usam os defaults do ambiente.
export const buildSentryConfig = ({
  stage = process.env.REACT_APP_STAGE,
  hostname = window.location.hostname,
} = {}) => {
  // Dev local carrega .env.development (stage=homolog) — não poluir o projeto de homolog.
  if (!REPORTING_STAGES.includes(stage) || isHostLocal(hostname)) {
    return null;
  }

  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    // eslint-disable-next-line no-console
    console.warn('Sentry desligado: REACT_APP_SENTRY_DSN ausente no build.');
    return null;
  }

  const buildNum = process.env.REACT_APP_BUILD_NUM || 'local';

  return {
    dsn,
    environment: stage,
    release: `backoffice-v3@${process.env.APP_VERSION}:${stage}-${buildNum}`,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: TRACES_SAMPLE_RATE[stage],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: ['top.GLOBALS'],
    denyUrls: [/\/node_modules\//i, /^chrome:\/\//i],
    beforeBreadcrumb: redigirBreadcrumb,
    beforeSend: redigirEvento,
    beforeSendTransaction: redigirEvento,
  };
};

export const setupSentry = (params) => {
  try {
    const config = buildSentryConfig(params);
    if (!config) return;
    Sentry.init(config);
  } catch (e) {
    // Observabilidade nunca derruba o boot do app. Loga só a mensagem —
    // o objeto de exceção inteiro pode carregar dado sensível.
    // eslint-disable-next-line no-console
    console.warn(`Sentry não inicializado: ${e instanceof Error ? e.message : 'erro desconhecido'}`);
  }
};
